import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Spin, Tooltip } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { useGetBankQuery } from "../../../redux/banks/bankAPI";
import {
  updateOpenModalControlPanelBank,
  updateControlPanelDrawerPlacement,
  updateBankSelected,
  updateServerId,
  updateChannelId,
} from "../../../redux/monitoring/monitoringSlice";
// COMPONENTS
import MonitoringElevatorsFullView from "../fullView/MonitoringElevatorsFullView";
import MonitoringTopView from "../topView/MonitoringTopView";
import MonitoringEscalatorsFullView from "../fullView/MonitoringEscalatorsFullView";
import MonitoringMovingWalkFullView from "../fullView/MonitoringMovingWalkFullView";
import MonitoringStatusView from "../statusView/MonitoringStatusView";
import MonitoringHeartBeat from "./MonitoringHeartBeat";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// INTERFACES
import { MonitoringViewTypeEnum } from "../../../interfaces/Monitoring.interface";
import { RootState } from "../../../app/store";
import { BankInterface } from "../../../interfaces/Bank.interface";
// CONSTANTS
import { BLANK_BANK } from "../../../constants/bank";
// UTILS
import { emptyFunctionDeclaration } from "../../../utils/utils";
import { floorIndexOrZero, readDoorFlags } from "../../../utils/bankFloorOpenings";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import formatUuidT from "../../../flatbuffers/display/uuid";
import { toLiftType } from "../../../flatbuffers/api/requestSerializers";
import { describeBankLoadError } from "./bankLoadError";
import OverlaySourcesOffline from "../overlay/OverlaySourcesOffline";
// SCOPE
import {
  useCanOpenControlPanel,
  useScopePredicate,
} from "../../../customHooks/useScopedPermission";
import { PermissionAction } from "../../../flatbuffers/generated/archive/permission-action";

function MonitoringBank({
  bankId,
  serverId,
  channelId,
  h,
  w,
  lifts: liftsFromLayout = [],
  bankFloors: floorsFromLayout = [],
}: {
  bankId: string;
  serverId: string;
  channelId: string;
  h: number;
  w: number;
  lifts?: any[];
  bankFloors?: any[];
}) {
  // Guard: a card mounted with no bankId (or non-string) means the parent
  // layout entry has no `idBank` — the bank query can never resolve, and the
  // card would silently render BLANK_BANK forever. Fail loudly instead so the
  // upstream layout-build bug is visible at first paint.
  if (typeof bankId !== "string" || bankId.trim() === "") {
    const msg = `[MonitoringBank] bankId is missing or empty (got ${JSON.stringify(
      bankId,
    )}) — layout entry has no idBank, bank query cannot fire. serverId=${JSON.stringify(
      serverId,
    )} channelId=${JSON.stringify(channelId)} liftsFromLayout=${
      liftsFromLayout?.length ?? 0
    } floorsFromLayout=${floorsFromLayout?.length ?? 0}`;
    // eslint-disable-next-line no-console
    console.error(msg);
    throw new Error(msg);
  }
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const SHOW_GRID_INFO = false;

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [token, setToken] = useState<string | null>(null);
  const [bank, setBank] = useState<BankInterface>({ ...BLANK_BANK });

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const monitoringGridType = useSelector((state: RootState) => state.monitoring.monitoringGridType);
  const enableDragAndDrop = useSelector((state: RootState) => state.monitoring.enableDragAndDrop);
  const theme = useSelector((state: RootState) => state.home.theme);
  // The bank name opens the control panel only when at least one of the panel's
  // tabs would render for this role (see CONTROL_PANEL_TAB_GATES); MASTER ADMIN
  // always. Deliberately NOT tied to the Banks switch: the Roles editor's
  // Monitoring Dashboard Banks block shows the card, it does not make the name
  // clickable. With no tab to show the name is inert and says nothing — a
  // permission denial is not an error.
  const canOpenBankControlPanel = useCanOpenControlPanel("bank");
  // VIEW-scope predicate — empty rows ⇒ allow-all, master admin ⇒ all. Banks are
  // filtered upstream in MonitoringGrid; here we hide both the lift CARDS and the
  // floor-axis ROWS a user lacks VIEW scope for, so the monitoring shaft never
  // surfaces lifts or floors outside their scope. Applied at render time so it
  // reacts to scope loading even if the bank data resolves first.
  const viewScopeAllows = useScopePredicate(PermissionAction.VIEW);
  const scopedBank = {
    ...bank,
    lifts: (bank.lifts ?? []).filter((l: any) => viewScopeAllows({ liftId: String(l.id) })),
    bankFloors: (bank.bankFloors ?? []).filter((f: any) =>
      viewScopeAllows({ bankFloorId: String(f.id) }),
    ),
  };

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  // refetchOnMountOrArgChange=true so a bank that gains action_banks
  // server-side (e.g. via the SeedBankControlActions reconcile path
  // running on Key/Bank/Lift change or at startup) shows the new
  // actions on the next monitoring page mount, without requiring the
  // operator to manually invalidate the RTK Query cache.
  const { data, isLoading, isFetching, error } = useGetBankQuery(
    { token, bankId },
    { skip: !token, refetchOnMountOrArgChange: true },
  );
  // Only a 404 means the bank is gone; bankLoadError.ts says what the other statuses mean.
  const bankLoadErrorText = error ? describeBankLoadError(error, bankId) : undefined;

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getDrawerPlacement = (e: any) => {
    const clickX = e.clientX;
    const screenWidth = window.innerWidth;
    if (clickX > screenWidth / 2) {
      dispatch(updateControlPanelDrawerPlacement("left"));
    } else {
      dispatch(updateControlPanelDrawerPlacement("right"));
    }
  };

  const onClickOpenDrawerBank = (e: any) => {
    getDrawerPlacement(e);
    dispatch(updateBankSelected(scopedBank));
    dispatch(updateServerId(serverId));
    dispatch(updateChannelId(channelId));
    dispatch(updateOpenModalControlPanelBank(true));
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    GETJwtToken().then(setToken);
  }, []);

  // Loud-error guard: when the bank query resolves to an error (e.g. HTTP 404
  // because the saved layout references a bank UUID that no longer exists in
  // the DB), we previously fell through silently and rendered BLANK_BANK with
  // only the HeartBeat dot — indistinguishable from a normal empty state. Make
  // the failure visible at the source so stale layouts surface immediately.
  useEffect(() => {
    if (error) {
      const status =
        typeof error === "object" && error !== null && "status" in error
          ? (error as { status: unknown }).status
          : "unknown";
      const errData =
        typeof error === "object" && error !== null && "data" in error
          ? (error as { data: unknown }).data
          : null;
      const msg = `[MonitoringBank] Bank query failed for bankId=${bankId} (status=${String(
        status,
      )}). Layout references a bank that the API cannot return — likely deleted/migrated. errorData=${JSON.stringify(
        errData,
      )}`;
      // eslint-disable-next-line no-console
      console.error(msg);
    }
  }, [error, bankId]);

  useEffect(() => {
    if (data && data.data) {
      const b = data.data;
      // Bank API may not embed lifts/floors — fall back to layout (cascader data)
      const resolvedLifts = b.lifts && b.lifts.length > 0 ? b.lifts : liftsFromLayout;
      // Normalize snake_case floor props to camelCase (handles both REST API
      // snake_case and FlatBuffer/cascader camelCase sources).
      //
      // frontDoor/rearDoor go through the shared readDoorFlags reader
      // (bankFloorOpenings.ts) rather than a bare `??` chain or a locally
      // re-derived resolveDoor helper — it already coerces the numeric 0/1
      // wire encoding, and both normalizeFloors and normalizeLiftFloors below
      // share it so the two can't silently drift apart the way
      // normalizeLiftFloors's frontDoor default once did.
      const normalizeFloors = (floors: any[]) =>
        floors.map((f: any) => {
          const doors = readDoorFlags(f, true, false);

          return {
            ...f,
            id: formatUuidT(f.id) || f.id,
            bankId: formatUuidT(f.bankId ?? f.bank_id) || f.bankId || f.bank_id,
            floorNo: f.floorNo ?? f.floor_no ?? 0,
            floorIndex: floorIndexOrZero(f),
            floorBelowLobby: f.floorBelowLobby ?? f.floor_below_lobby ?? false,
            frontDoor: doors.front,
            rearDoor: doors.rear,
          };
        });
      // REST API returns snake_case "bank_floors"; FlatBuffer/cascader uses "bankFloors"
      const rawApiBankFloors: any[] = b.bankFloors ?? b.bank_floors ?? [];
      // Pick the source array first, like resolvedLifts above does, so the
      // (usual) embedded-floors case normalizes once instead of normalizing
      // the discarded layout fallback on every response too.
      const resolvedFloors = normalizeFloors(
        rawApiBankFloors.length > 0 ? rawApiBankFloors : floorsFromLayout,
      );
      // Normalize UuidT objects to strings — FlatBuffer unpack returns UuidT for all
      // UUID fields, but downstream components use String(id) which produces
      // "[object Object]" instead of the UUID string, breaking context lookups.
      // Front defaults to true, matching every other reader of this same
      // per-lift-floor shape (extractLiftDoorMaps, and TerminalPage.tsx's
      // and StepLiftsFloors.tsx's own normalizeFloors/loadExistingLifts)
      // and the front-open/rear-closed convention documented on
      // readDoorFlags and extractBankFloorOpenings. This previously
      // defaulted to false here only — a pre-existing, standalone bug
      // (introduced in 2a95d6f64 with no documented rationale, most likely
      // copy-pasted from the rearDoor fallback below it) that made a lift's
      // front-door indicator read as absent for any lift_floors row that
      // did not explicitly carry the flag.
      const normalizeLiftFloors = (liftFloorsRaw: any[] | undefined) =>
        (liftFloorsRaw ?? []).map((lf: any) => {
          const doors = readDoorFlags(lf, true, false);

          return {
            ...lf,
            id: formatUuidT(lf.id) || lf.id,
            bankFloorId:
              formatUuidT(lf.bankFloorId ?? lf.bank_floor_id) || lf.bankFloorId || lf.bank_floor_id,
            liftId: formatUuidT(lf.liftId ?? lf.lift_id) || lf.liftId || lf.lift_id,
            frontDoor: doors.front,
            rearDoor: doors.rear,
          };
        });
      const normalizedLifts = resolvedLifts.map((l: any) => ({
        ...l,
        id: formatUuidT(l.id) || l.id,
        bankId: formatUuidT(l.bankId ?? l.bank_id) || l.bankId || l.bank_id,
        liftFloors: normalizeLiftFloors(l.liftFloors ?? l.lift_floors),
      }));
      const bankIdStr = formatUuidT(b.id) || b.id;
      // REST API may return type as a string ("ELEVATOR"); normalize to the numeric FlatBuffer enum
      // Store the FULL lift set; lift-VIEW scope is applied at render time
      // (see scopedBank) so it reacts to scope loading regardless of order.
      setBank({
        ...b,
        id: bankIdStr,
        type: toLiftType(b.type),
        lifts: normalizedLifts,
        bankFloors: resolvedFloors,
      });
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.log(
          `[BANK LOADED] "${b.name}" bank.id=${formatUuidT(b.id) ?? b.id} floors=${
            resolvedFloors.length
          } lifts=[${resolvedLifts
            .map((l: any) => `${l.name}:${formatUuidT(l.id) ?? l.id}`)
            .join(", ")}]`,
        );
      }
    }
  }, [data]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div
      className={`monitoring__card__${theme} generalStyles__width100`}
      style={{ height: "auto" }}
    >
      {/** *********************************************************** */}
      {/** HEADER */}
      <div className="monitoring__card__header">
        <div className="generalStyles__flex">
          <MonitoringHeartBeat bankId={formatUuidT(bank.id as any) ?? ""} type="TOOLTIP" />
          <div
            className="generalStyles__textEllipsis"
            role="button"
            tabIndex={0}
            onKeyDown={emptyFunctionDeclaration}
            style={canOpenBankControlPanel ? undefined : { cursor: "default" }}
            onClick={(e: any) => {
              if (!enableDragAndDrop && canOpenBankControlPanel) {
                onClickOpenDrawerBank(e);
              }
            }}
          >
            <Tooltip
              placement="topLeft"
              title={
                bankLoadErrorText
                  ? bankLoadErrorText.tooltip
                  : `${bank.name} ${SHOW_GRID_INFO ? `${h}-${w}` : ""}`
              }
            >
              <h6 style={bankLoadErrorText ? { color: "#ff4d4f" } : undefined}>
                {bankLoadErrorText ? bankLoadErrorText.headline : bank.name}
              </h6>
            </Tooltip>
          </div>
        </div>
        {/* Under the title, not beside it: on a narrow card a badge in the
            title's row squeezes the bank's own name to nothing. */}
        <OverlaySourcesOffline bankId={formatUuidT(bank.id as any) ?? null} />
      </div>
      <Spin spinning={isFetching || isLoading}>
        {/* ************************************************* */}
        {/** OVER HEAD VIEW */}
        {monitoringGridType === MonitoringViewTypeEnum.OVER_HEAD_VIEW && (
          <>
            {bank.type === LiftType.ELEVATOR && (
              <MonitoringTopView bank={scopedBank} serverId={serverId} channelId={channelId} />
            )}
            {bank.type === LiftType.ESCALATOR && (
              <MonitoringEscalatorsFullView
                bank={scopedBank}
                serverId={serverId}
                channelId={channelId}
                enableActions
              />
            )}
            {bank.type === LiftType.MOVINGWALK && (
              <MonitoringMovingWalkFullView
                bank={scopedBank}
                serverId={serverId}
                channelId={channelId}
              />
            )}
          </>
        )}
        {/* ************************************************* */}
        {/** STATUS VIEW */}
        {monitoringGridType === MonitoringViewTypeEnum.STATUS_VIEW && (
          <MonitoringStatusView bank={scopedBank} serverId={serverId} channelId={channelId} />
        )}
        {/* ************************************************* */}
        {/** EXPANDED VIEW */}
        {monitoringGridType === MonitoringViewTypeEnum.EXPANDED_VIEW && (
          <div className="mt-2">
            {bank.type === LiftType.ELEVATOR && (
              <MonitoringElevatorsFullView
                bank={scopedBank}
                serverId={serverId}
                channelId={channelId}
                enableActions
              />
            )}
            {bank.type === LiftType.ESCALATOR && (
              <MonitoringEscalatorsFullView
                bank={scopedBank}
                serverId={serverId}
                channelId={channelId}
                enableActions
              />
            )}
            {bank.type === LiftType.MOVINGWALK && (
              <MonitoringMovingWalkFullView
                bank={scopedBank}
                serverId={serverId}
                channelId={channelId}
              />
            )}
          </div>
        )}
      </Spin>
    </div>
  );
}

export default React.memo(MonitoringBank);
